import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { constantsData, importerConfigData, statusTypesData } from "@/data";
import { createReleaseMotionArtworkMutation, getReleaseMotionArtworksListQuery } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useCreateReleaseMotionArtwork = (releaseId, withoutRefetch, withoutAlert) => {
    const [controller, setController] = useState(null);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [createReleaseMotionArtworkAction] = useMutation(createReleaseMotionArtworkMutation, {
        ...(!withoutRefetch && {
            awaitRefetchQueries: true,
            refetchQueries: [
                {
                    query: getReleaseMotionArtworksListQuery,
                    variables: {
                        where: {
                            release: {
                                id: { equals: releaseId },
                            },
                        },
                    },
                },
            ],
        }),
    });

    const createReleaseMotionArtwork = async (fileId, assetType, importerData) => {
        if (window?.__RUNTIME_CONFIG__?.ENABLE_DEBUG_LOGS === "true" && importerData) {
            console.log("releaseIdFromCreateReleaseMotionArtworkHook", releaseId);
        }

        try {
            const newController = new AbortController();

            setController(newController);

            if (importerData) importerData?.setCurrentStep(importerConfigData.steps.creatingMotionArtwork);

            await createReleaseMotionArtworkAction({
                context: {
                    fetchOptions: { signal: newController.signal },
                },
                onCompleted: ({ createOneReleaseMotionArtwork }) => {
                    if (importerData?.uploadingMotionArtworkFile) {
                        importerData?.setCreatedMotionArtworks((prev) => [
                            ...prev,
                            {
                                ...importerData?.motionArtwork,
                                id: createOneReleaseMotionArtwork?.id,
                            },
                        ]);
                    }

                    if (!withoutAlert) {
                        dispatch(
                            openAlert(
                                t("messages.uploadMsgs.success", { entity: t("labels.video") }),
                                statusTypesData.success,
                            ),
                        );
                    }
                },
                variables: {
                    data: {
                        assetType,
                        file: {
                            connect: { id: fileId },
                        },
                        release: {
                            connect: { id: importerData ? importerData?.release?.id : releaseId },
                        },
                    },
                },
            });
        } catch (err) {
            if (!withoutAlert) {
                displayErrorHandler(
                    err.message === constantsData.failedToFetch
                        ? t("messages.networkFailure")
                        : `${t("messages.createMsgs.fail", { entity: t("labels.motionArtworks") })}, ${err.message}`,
                    dispatch,
                    openAlert,
                );
            }

            if (importerData) {
                if (importerData?.uploadingMotionArtworkFile) {
                    dispatch(
                        importerData?.setInvalidImporterRelease({
                            motionArtwork: {
                                data: importerData?.motionArtwork,
                                message:
                                    err.message === constantsData.failedToFetch
                                        ? t("messages.networkFailure")
                                        : `${t("messages.importerMsgs.createVideoFailed")}, ${err.message}`,
                            },
                            originalData: importerData?.originalData,
                            release: { data: importerData?.release },
                            ...(err.message === constantsData.failedToFetch && { isNetworkFailed: true }),
                        }),
                    );

                    importerData?.setStatus((prev) => ({
                        ...prev,
                        motionArtwork: {
                            ...prev.motionArtwork,
                            loading: false,
                            success: false,
                        },
                    }));
                }
            }
        } finally {
            setController(null);

            if (importerData) importerData?.setCurrentStep(null);
        }
    };

    return {
        cancelRequest: () => controller && controller.abort(),
        createReleaseMotionArtwork,
    };
};

export default useCreateReleaseMotionArtwork;
